import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { STATS } from "../lib/data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1, delay: 0.25 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }),
};

const HERO_IMG = "https://images.unsplash.com/photo-1762279389020-eeeb69c25813?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const lines = ["We back conviction", "with real capital,", "not conditions."];

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Trading data" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1400px] w-full mx-auto px-5 sm:px-8 pb-16 sm:pb-24 pt-32">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="font-mono text-xs uppercase tracking-[0.3em] text-brass mb-8 flex items-center gap-3">
          <span className="w-8 h-px bg-brass" /> Proprietary Trading · Est. 2026
        </motion.div>

        <h1 data-testid="hero-heading" className="font-serif font-light text-[13vw] leading-[0.92] sm:text-7xl lg:text-8xl tracking-tight text-bone max-w-5xl">
          {lines.map((l, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8">
          <p className="text-mute text-base sm:text-lg max-w-md leading-relaxed">
            Goose the Gander is a proprietary trading firm built for traders with edge. We finance talent, engineer the infrastructure, and share the upside. Discipline today. Freedom tomorrow.
          </p>
          <button
            data-testid="hero-apply-btn"
            onClick={() => document.querySelector("#apply")?.scrollIntoView({ behavior: "smooth" })}
            className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] bg-brass text-ink px-7 py-4 hover:bg-bone transition-colors shrink-0"
          >
            Apply to trade
            <ArrowDownRight size={16} className="group-hover:translate-x-1 group-hover:translate-y-1 transition-transform" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="relative z-10 border-t border-line bg-ink/40 backdrop-blur-sm">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 sm:grid-cols-3 sm:divide-x divide-line">
          {STATS.map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`} className="py-6 sm:py-8 px-4 sm:first:pl-0 border-b border-line sm:border-b-0">
              <div className="font-mono text-2xl sm:text-4xl font-light text-bone">{s.value}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
